import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useInfiniteScroll
 *
 * A custom hook that manages infinite-scroll pagination state and wires up
 * the scroll event listener automatically.
 *
 * @param {function} fetchFn
 *   Async function that accepts `{ page, ...extraParams }` and returns a
 *   paginated response shaped as `{ results, total_pages, ... }`.
 *
 * @param {object} [options]
 * @param {object}  [options.extraParams={}]
 *   Additional params forwarded to `fetchFn` on every call (e.g. a search
 *   keyword). Changing this value resets the list back to page 1.
 * @param {boolean} [options.enabled=true]
 *   When `false` the hook will not fetch or register the scroll listener.
 *   Useful for controlling when a modal/section is open.
 * @param {number}  [options.bottomThreshold=50]
 *   How many pixels from the bottom of the scroll container trigger the next
 *   page fetch.
 *
 * @returns {{
 *   items: Array,
 *   isLoading: boolean,
 *   scrollRef: React.RefObject,
 *   reset: function,
 * }}
 *
 * @example
 * const { items, isLoading, scrollRef } = useInfiniteScroll(
 *   fetchDoctorList,
 *   { enabled: isOpen }
 * );
 */
export function useInfiniteScroll(fetchFn, options = {}) {
    const {
        extraParams = {},
        enabled = true,
        bottomThreshold = 50,
        onError,
    } = options;

    const [items, setItems] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const requestInFlightRef = useRef(false);
    const fetchedPagesRef = useRef(new Set());

    // scrollRef is a ref-callback so that the scroll effect re-runs the moment
    // the consumer assigns a DOM element (even after the initial mount).
    const scrollElementRef = useRef(null);
    const [scrollEl, setScrollEl] = useState(null);
    const scrollRef = useCallback((el) => {
        scrollElementRef.current = el;
        setScrollEl(el);
    }, []);

    // Stable ref so the scroll handler always reads the latest values without
    // needing to be re-created on every state change.
    const stateRef = useRef({ currentPage, totalPages, isLoading });
    useEffect(() => {
        stateRef.current = { currentPage, totalPages, isLoading };
    }, [currentPage, totalPages, isLoading]);

    const fetchPage = useCallback(
        async (page, reset = false) => {
            if (requestInFlightRef.current) return;
            if (!reset && fetchedPagesRef.current.has(page)) return;

            requestInFlightRef.current = true;
            fetchedPagesRef.current.add(page);
            setIsLoading(true);
            try {
                const response = await fetchFn({ page, ...extraParams });
                setItems((prev) =>
                    reset ? response.results : [...prev, ...response.results]
                );
                setCurrentPage(page);
                setTotalPages(response.total_pages ?? 1);
            } catch (error) {
                fetchedPagesRef.current.delete(page);
                if (onError) {
                    onError(error);
                } else {
                    console.error("Error fetching page:", error);
                }
            } finally {
                requestInFlightRef.current = false;
                setIsLoading(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [fetchFn, JSON.stringify(extraParams), onError]
    );

    // Reset and re-fetch page 1 whenever extraParams or enabled changes.
    useEffect(() => {
        if (!enabled) {
            requestInFlightRef.current = false;
            fetchedPagesRef.current = new Set();
            setItems([]);
            setCurrentPage(1);
            setTotalPages(1);
            return;
        }
        requestInFlightRef.current = false;
        fetchedPagesRef.current = new Set();
        setItems([]);
        setCurrentPage(1);
        setTotalPages(1);
        fetchPage(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, JSON.stringify(extraParams)]);

    // Scroll listener - re-registers whenever the element, fetchPage, or enabled changes.
    useEffect(() => {
        if (!enabled) return;
        const el = scrollEl;
        if (!el) return;

        const handleScroll = () => {
            const { currentPage: cp, totalPages: tp, isLoading: loading } =
                stateRef.current;
            if (loading || cp >= tp) return;

            const nearBottom =
                el.scrollTop + el.clientHeight >= el.scrollHeight - bottomThreshold;
            if (!nearBottom) return;

            fetchPage(cp + 1);
        };

        el.addEventListener("scroll", handleScroll);
        return () => el.removeEventListener("scroll", handleScroll);
    }, [enabled, fetchPage, bottomThreshold, scrollEl]);

    /** Manually reset the list and re-fetch page 1. */
    const reset = useCallback(() => {
        setItems([]);
        setCurrentPage(1);
        setTotalPages(1);
        fetchPage(1, true);
    }, [fetchPage]);

    return { items, isLoading, scrollRef, reset };
}
